import React, { useState, useEffect, useCallback } from 'react';
import { Combobox } from '@headlessui/react';
import { X, Loader } from 'lucide-react'; // Import Loader from lucide-react
import { authorizedServerFetch } from '@/actions/server.actions';
import Avatar from '../next-ui/Avatar';

export default function RecipientSelector({ value = [], onChange }) {
  const [query, setQuery] = useState('');
  const [selectedPeople, setSelectedPeople] = useState(value);
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Debounce function to limit API requests
  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  // Fetch results from the API with debouncing
  const fetchSearchResults = useCallback(
    debounce(async (query) => {
      if (query.trim().length === 0) {
        setSearchResults([]);
        return;
      }

      setIsLoading(true);
      setError('');
      try {
        const { ok, data } = await authorizedServerFetch(
          'users/search',
          {},
          { search: query },
        );
        if (ok) {
          const userList = data?.data || [];
          const contacts = userList.map((i) => ({
            name: `${i.first_name} ${i.last_name}`,
            id: i.id,
            email: i.email,
            avatar: i.avatar, // Ensure avatar URL is included
          }));
          setSearchResults(contacts);
        } else {
          setError('Failed to fetch results.');
        }
      } catch (error) {
        console.error('Error fetching search results:', error);
        setError('An error occurred while fetching results.');
      } finally {
        setIsLoading(false);
      }
    }, 300),
    [],
  );

  useEffect(() => {
    fetchSearchResults(query);
  }, [query, fetchSearchResults]);

  const handleSelect = (person) => {
    if (!selectedPeople.some((p) => p.id === person.id)) {
      const updatedSelection = [...selectedPeople, person];
      setSelectedPeople(updatedSelection);
      onChange(updatedSelection);
      setQuery('');
    }
  };

  const handleRemove = (person) => {
    const updatedSelection = selectedPeople.filter((p) => p.id !== person.id);
    setSelectedPeople(updatedSelection);
    onChange(updatedSelection);
  };

  return (
    <div className="w-full max-w-full">
      {' '}
      {/* Ensure full width */}
      {/* Selected recipients as chips */}
      <div className="mb-2 flex flex-wrap gap-2">
        {selectedPeople.map((person) => (
          <div
            key={person.id}
            onClick={() => handleRemove(person)}
            className="flex cursor-pointer items-center rounded-full bg-primary-light-1 px-3 py-1 text-primary">
            <span className="mr-2">{person.name}</span>
            <button className="text-primary">
              <X size={20} />
            </button>
          </div>
        ))}
      </div>
      {/* Recipient search and selection */}
      <Combobox as="div" onChange={handleSelect}>
        <div className="relative">
          <Combobox.Input
            className="w-full rounded-lg border p-2 focus:outline-[#33CCCC]"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search recipients..."
            value={query}
          />
          {isLoading && (
            <div className="absolute right-0 top-0 flex items-center justify-center pr-4 pt-2">
              <Loader size={24} className="animate-spin text-primary" />
            </div>
          )}
          {error && (
            <div className="absolute right-0 top-0 p-2 text-red-500">
              {error}
            </div>
          )}
          {searchResults.length > 0 && (
            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-white shadow-lg">
              {searchResults.map((person) => (
                <Combobox.Option
                  key={person.id}
                  value={person}
                  className="cursor-pointer p-2 hover:bg-gray-100">
                  {({ active }) => (
                    <div
                      className={`flex items-center ${
                        active ? 'bg-gray-200' : ''
                      }`}>
                      <Avatar src={person.avatar} alt={person.name} size="sm" />
                      <span className="ml-2">{person.name}</span>
                    </div>
                  )}
                </Combobox.Option>
              ))}
            </Combobox.Options>
          )}
        </div>
      </Combobox>
    </div>
  );
}
